import { Box, Typography } from '@mui/material';
import { AxiosError } from 'axios';
import { FC, useContext, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate, useSearchParams } from 'react-router-dom';

import { routes } from 'src/constants/routes';
import { StepList } from 'src/features/step-list';
import { DiscountBanner } from 'src/components/discount-banner';
import { PaymentPlan } from 'src/features/payment-plan';
import { RoomCard } from 'src/features/room-card';
import { StepEnum } from 'src/features/step-list/constants';
import { Currency } from 'src/shared/enum/Currency';
import { useGetAmendData } from 'src/lib/hooks/use-get-amend-data';
import { Spinner } from 'src/components/spinner';
import { FailurePage } from 'src/pages/failure';
import { ErrorMessage } from 'src/shared/enum/ErrorMessage';
import { BookingUpdateFailurePage } from 'src/pages/booking-update-failure';
import { BookingUpdateExpiredPage } from 'src/pages/booking-update-expired';
import { BookingUpdatedPage } from 'src/pages/booking-updated';
import { LoginDialog } from 'src/features/login-dialog';
import { AuthContext } from 'src/providers/auth-context';
import { RoommateData } from 'src/features/multi-occupancy/types';
import { setAmendRoommatesStorage, setVitaRefStorage } from 'src/utils/local-storage-versioning';

import { styles } from '../styles';

import { AmendAgreementsContainer } from './amend-agreements-container';
import { AmendRoommatesContainer } from './amend-roommates-container';

const processError = (error: AxiosError) => {
    switch (error.message) {
        case ErrorMessage.AMEND_NOT_REQUESTED:
        case ErrorMessage.AMEND_ADMIN_FEE_REQUIRED:
        case ErrorMessage.AMEND_GENERIC_ERROR:
            return <BookingUpdateFailurePage />;
        case ErrorMessage.AMEND_WINDOW_EXPIRED:
            return <BookingUpdateExpiredPage />;
        case ErrorMessage.AMEND_ALREADY_PROCESSED:
            return <BookingUpdatedPage />;
        case ErrorMessage.AMEND_TOO_MANY_OCCUPANTS:
            return (
                <BookingUpdateFailurePage
                    titleKey="booking_updated_amend_occupant_error_title"
                    subtitleKey="booking_updated_amend_occupant_error_content"
                />
            );
        default:
            return <FailurePage />;
    }
};

export const AmendBookingContainer: FC = () => {
    const navigate = useNavigate();
    const [searchParams] = useSearchParams();
    const userState = useContext(AuthContext);
    const { t } = useTranslation();
    const [roommates, setRoommates] = useState<RoommateData[]>([]);

    const vitaRef = searchParams.get('vitaRef');
    const {
        data: amendData,
        isFetching: isAmendDataFetching,
        error: amendError,
    } = useGetAmendData(vitaRef);

    const [activeStep, setActiveStep] = useState<StepEnum | null>(null);

    useEffect(() => {
        if (!amendData) return;

        setActiveStep(amendData.dual_occupant_amend ? StepEnum.Roommates : StepEnum.Agreements);
    }, [amendData]);

    useEffect(() => {
        if (vitaRef) {
            setVitaRefStorage(vitaRef);
            setAmendRoommatesStorage([]);
            setRoommates([]);
        }
    }, [vitaRef]);

    if (!vitaRef) {
        navigate(routes.failure.index);
        return null;
    }

    if (isAmendDataFetching)
        return (
            <Box css={styles.layout}>
                <Spinner />
            </Box>
        );

    if (amendError) {
        return processError(amendError);
    }

    if (!userState.loggedIn) {
        return (
            <Box css={styles.layout}>
                <Box css={styles.leftSide}>
                    <LoginDialog open closeFn={() => {}} />
                </Box>
            </Box>
        );
    }

    if (!amendData) return <FailurePage />;

    const steps = [StepEnum.Agreements];

    if (amendData.admin_fee_required) steps.push(StepEnum.Payment);
    if (amendData.dual_occupant_amend) steps.unshift(StepEnum.Roommates);

    const hasDiscount = !!amendData.payment_plan.promotion;

    const setRoommatesInStateAndLocalStorage = (roommatesData: RoommateData[]) => {
        setRoommates(roommatesData);
        setAmendRoommatesStorage(roommatesData);
    };

    return (
        <Box css={styles.layout}>
            <Box css={styles.leftSide}>
                {activeStep && (
                    <StepList
                        activeStep={activeStep}
                        steps={steps}
                        paymentRequired={amendData.admin_fee_required}
                    />
                )}
                {activeStep === StepEnum.Roommates && (
                    <AmendRoommatesContainer
                        numberOfStudents={amendData.number_of_students}
                        setActiveStep={() => setActiveStep(StepEnum.Agreements)}
                        setRoommates={setRoommatesInStateAndLocalStorage}
                        roommates={roommates}
                    />
                )}

                {activeStep === StepEnum.Agreements && (
                    <AmendAgreementsContainer
                        building={amendData.building}
                        amendData={amendData}
                        vitaRef={vitaRef}
                        processError={processError}
                        handleGoBack={() => setActiveStep(StepEnum.Roommates)}
                        roommates={roommates}
                    />
                )}
            </Box>

            <Box css={styles.rightSide}>
                <RoomCard
                    withSmallImage
                    roomData={amendData.room}
                    isAgreementsPage
                    building={amendData.building}
                    termDuration={amendData.term_length}
                    termStartDate={amendData.start_date}
                    termEndDate={amendData.end_date}
                    numberOfStudents={amendData.number_of_students}
                />
                <Box css={styles.paymentPlanWrapper}>
                    {hasDiscount && (
                        <DiscountBanner>
                            <Typography variant="body3">{t('discount_applied_info')}</Typography>
                        </DiscountBanner>
                    )}
                    <Box css={styles.overviewPayment}>
                        <PaymentPlan
                            showCta={false}
                            withHeaderStipe={false}
                            currency={
                                amendData ? (amendData.room.currency as Currency) : Currency.GBP
                            }
                            disabled={false}
                            createdPaymentPlan={amendData.payment_plan}
                            isGapWeekEligible={amendData.gap_week_required}
                        />
                    </Box>
                </Box>
            </Box>
        </Box>
    );
};
